import { EnergyBridgePage } from '../../client'
import calcVerticalHeight from '../../utils/calcVerticalHeight'
import COLORS from '../../utils/colorScale'
import { defaultConfig, layoutTemplate } from '../../utils/plotly'

import useVertical from '../../hooks/useVertical'
import Plot from '../shared/Plot'

const PlotEnergyBridgeTotals = ({
  revision,
  energyBridge,
  isLoading,
}: {
  revision: number
  energyBridge: EnergyBridgePage
  isLoading: boolean
}) => {
  const isVertical = useVertical()

  if (isLoading) return null

  const {
    budget_gwh,
    weather_gwh,
    potential_prod_gwh,
    turb_perf_gwh,
    turb_av_gwh,
    bop_av_gwh,
    electrical_eff_gwh,
    comp_curtailment_gwh,
    non_comp_curtailment_gwh,
    remainder_gwh,
    actual_gwh,
  } = energyBridge.totals

  const budget = budget_gwh || 1
  const actual = actual_gwh || 0
  const potential = potential_prod_gwh || 0

  // Pico visual do waterfall + folga para textposition: 'outside'
  const yPeak = Math.max(
    budget_gwh || 0,
    potential_prod_gwh || 0,
    actual_gwh || 0,
    (budget_gwh || 0) + (weather_gwh || 0),
    (potential_prod_gwh || 0) +
    Math.max(
      0,
      (turb_perf_gwh || 0) +
      (turb_av_gwh || 0) +
      (bop_av_gwh || 0) +
      (electrical_eff_gwh || 0) +
      (comp_curtailment_gwh || 0) +
      (non_comp_curtailment_gwh || 0) +
      (remainder_gwh || 0)
    )
  )
  const yMax = yPeak > 0 ? yPeak * 1.28 : 1

  const layout = {
    template: layoutTemplate,
    margin: { b: isVertical ? 160 : 100, t: 30, l: 72, r: 40 },
    hovermode: 'x',
    showlegend: false,
    yaxis: {
      linewidth: 2,
      gridwidth: 2,
      mirror: 'ticks',
      fixedrange: true,
      automargin: true,
      title: 'Production [GWh]',
      range: [0, yMax],
    },
    xaxis: {
      linewidth: 2,
      gridwidth: 2,
      mirror: 'ticks',
      fixedrange: true,
      tickangle: -35,
      automargin: true,
    },
  }
  const config = { ...defaultConfig, displayModeBar: false }
  const style = {
    height: calcVerticalHeight({
      gridSize: isVertical ? 1 : 2,
      elementSize: 1,
      customOffset: isVertical ? '5rem' : '2rem',
    }),
    minHeight: '22rem',
    minWidth: isVertical ? '900px' : '500px',
  }

  const formatText = (value: number) =>
    `${value.toFixed(1)} GWh<br>(${((value / budget) * 100).toFixed(1)}%)`
  const formatAbsolute = (value: number) => `${value.toFixed(1)} GWh`

  const data = [
    {
      name: 'Energy Bridge',
      type: 'waterfall',
      decreasing: { marker: { color: COLORS.cinza_medio } },
      increasing: { marker: { color: COLORS.azul_escuro } },
      totals: { marker: { color: COLORS.verde } },
      measure: [
        'absolute',
        'relative',
        'absolute',
        'relative',
        'relative',
        'relative',
        'relative',
        'relative',
        'relative',
        'relative',
        'absolute',
      ],
      text: [
        formatAbsolute(budget),
        formatText(weather_gwh || 0),
        formatAbsolute(potential),
        formatAbsolute(turb_perf_gwh || 0),
        formatAbsolute(turb_av_gwh || 0),
        formatAbsolute(bop_av_gwh || 0),
        formatAbsolute(electrical_eff_gwh || 0),
        formatAbsolute(comp_curtailment_gwh || 0),
        formatAbsolute(non_comp_curtailment_gwh || 0),
        formatAbsolute(remainder_gwh || 0),
        `${formatAbsolute(actual)}<br>(${(((actual - budget) / budget) * 100).toFixed(1)}%)`,
      ],
      textposition: 'outside',
      cliponaxis: false,
      constraintext: 'none',
      textfont: { size: 11 },
      x: [
        'Budget Production',
        '(Δ) Weather',
        'Potential Production',
        '(Δ) Turbine Performance',
        '(Δ) Turbine Av.',
        '(Δ) Internal BOP Av.',
        '(Δ) Electrical Efficiency',
        '(Δ) Compensated Curtailment',
        '(Δ) Non-Compensated Curtailment',
        '(Δ) Uncertainty',
        'Actual Production',
      ],
      y: [
        budget_gwh,
        weather_gwh,
        potential_prod_gwh,
        turb_perf_gwh,
        turb_av_gwh,
        bop_av_gwh,
        electrical_eff_gwh,
        comp_curtailment_gwh,
        non_comp_curtailment_gwh,
        remainder_gwh,
        actual_gwh,
      ],
    },
  ]

  return (
    <div
      className='pf-v5-u-p-sm'
      style={{ overflowX: 'auto', overflowY: 'visible', width: '100%' }}
    >
      <Plot
        divId='EnergyBridgeTotalsPlot'
        useResizeHandler
        data={data}
        layout={layout}
        config={config}
        style={style}
        key={revision}
      />
    </div>
  )
}

export default PlotEnergyBridgeTotals
